import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import BaseController from "../../common/base/BaseController";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtSearchBar from "../../components/NtSearchBar";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtTabController from "../../components/NtTabController";
import NtModal from "../../components/NtModal";
import {
  asyncStorageSetData,
  parseDisplayDate,
  showToast,
} from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import hardwarecsvcolumndata from "../data/hardwarecsvcolumndata";
import { fetchHardware } from "../../api/hardware";
import NtHardwareItem from "../components/NtHardwareItem";
import NtHardwareNewForm from "../components/NtHardwareNewForm";

class HardwareController extends BaseController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    totalItems: 0,
    searchText: "",
    searchFilter: null,
    selectedTab: "all",
    page: 1,
  };

  column_location = "@hardware_column_storage";
  addModalRef = React.createRef();

  tabs = [
    { id: "all", title: "All" },
    { id: "pending", title: "Pending" },
    { id: "archived", title: "Archived" },
  ];

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      hardwarecsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }


  handleDataFetch = async () => {
    this.setState({ loading: true });
    const response = await fetchHardware(
      this.state.selectedTab,
      this.state.page,
      settings.pageSizeLoad,
      this.state.searchText,
      this.state.searchFilter,
    );
    Logger("Hardware fetch response", response);
    if (response.ok) {
      this.setState({
        data: response.data?.data || [],
        totalItems: response.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const response = await fetchHardware(
      this.state.selectedTab,
      1, 100000,
      this.state.searchText,
      this.state.searchFilter,
    );
    return response.ok ? response.data.data : [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "creationdate") return `"${parseDisplayDate(item[column])}"`;
    return item[column];
  };

  handleSearch = (text) => {
    this.setState({ searchText: text, page: 1 }, () => this.handleDataFetch());
  };

  handleTab = (id) => {
    if (id === this.state.selectedTab) return;
    this.setState({ selectedTab: id, searchText: "", data: [], page: 1 }, () =>
      this.handleDataFetch(),
    );
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  renderAddNewForm = () => (
    <NtModal ref={this.addModalRef}>
      <NtHardwareNewForm
        onCancel={() => this.addModalRef.current?.dismiss()}
        onCreate={() => {
          this.setState({ page: 1 }, () => this.handleDataFetch());
          this.addModalRef.current?.dismiss();
          showToast("Success", "Successfully created the new hardware configuration");
        }}
      />
    </NtModal>
  );

  renderRow = (item, index) => (
    <NtHardwareItem
      key={(item.mac || "") + index}
      item={item}
      columns={this.state.columns}
      onUpdate={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", "Device updated");
      }}
      onDelete={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", "Device removed");
      }}
    />
  );

  render() {
    const count = this.state.totalItems || 0;
    const subtitle = `Provisioned phones and SIP devices · ${count} device${count === 1 ? "" : "s"}`;

    const actions = (
      <NtPageActionButton
        icon="plus"
        title="Add Device"
        variant="primary"
        onPress={() => this.addModalRef.current?.show()}
      />
    );

    const filters = (
      <NtTabController
        options={this.tabs}
        selection={this.tabs.find((t) => t.id === this.state.selectedTab)}
        onPress={(opt) => this.handleTab(opt.id)}
      />
    );

    const searchBar = (
      <NtSearchBar value={this.state.searchText} onChangeText={this.handleSearch} />
    );

    return (
      <NtRootPageContainer
        pageTitle="Hardware"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection
          filters={filters}
          search={searchBar}
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: 35 }}
              columns={hardwarecsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="hardware"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAddNewForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default HardwareController;
